import { ColorValue } from 'react-native';

// ============================================================================
// SEMANTIC TOKENS — organized by ROLE, not raw hex, so a theme swap (dark/gold
// rebrand, or the light/dark toggle below) only needs to change the raw value
// sets here, never the ~198 call sites that consume them.
//
// Legacy shapes (colors.primary, colors.white, colors.textDark, colors.text.*,
// colors.background.primary/secondary/disabled, colors.borders.*, ...) are
// kept alongside the new colors.brand/text/background/border/status/literal
// groups, pointing at the same underlying values, so existing call sites keep
// working unchanged.
//
// LIGHT/DARK TOGGLE: `colors`/`shadows` below are mutable, module-level
// objects (not frozen literals) so setColorScheme() can reassign their
// contents in place via Object.assign. This does NOT repaint already-rendered
// screens live — ~198 files call StyleSheet.create at module scope, which
// bakes in whatever colors.X resolved to at that module's *first* evaluation
// and never re-reads it. setColorScheme() only matters for correctness on the
// *next* module evaluation, i.e. after a reload — see ThemeContext.tsx, which
// calls setColorScheme() synchronously before first render, then triggers a
// full app reload whenever the user actually changes the preference.
// ============================================================================

/** Converts a '#RRGGBB' hex string to 'rgba(r, g, b, alpha)' — used to derive
 * transparent/tinted variants from a raw palette's solid colors, matching the
 * exact rgba() string format these tokens shipped with previously (rather than
 * '#RRGGBBAA' hex-alpha, which every consumer of these strings hasn't been
 * verified against, even though modern web/CSS supports it). */
function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

type RawPalette = {
  primary: ColorValue;
  primaryLight: ColorValue;
  secondary: ColorValue;
  accent: ColorValue;
  chrome: ColorValue;
  textPrimary: ColorValue;
  textSecondary: ColorValue;
  textDisabled: ColorValue;
  textOnPrimary: ColorValue;
  white: ColorValue;
  black: ColorValue;
  bgBase: ColorValue;
  bgSecondary: ColorValue;
  bgDisabled: ColorValue;
  overlay: ColorValue;
  success: ColorValue;
  warning: ColorValue;
  error: ColorValue;
  info: ColorValue;
  borderSubtle: ColorValue;
  borderMedium: ColorValue;
  borderStrong: ColorValue;
  /** Alpha for primaryTransparent/accentTransparent — a tinted-surface accent
   * (e.g. a selected-row background). Differs per mode in the source-of-truth
   * CSS (--imported-accent): .15 in dark, .12 in light. */
  accentTintAlpha: number;
};

// Dark navy + gold palette (the app's current/default theme).
const darkRaw: RawPalette = {
  primary: '#DA942C',       // gold — accent elements (buttons, links, active states)
  primaryLight: '#f0b558',
  secondary: '#b87216',
  accent: '#f0b558',

  // Dedicated chrome fill for large dominant surfaces (headers, tab bars) —
  // deliberately NOT gold, since gold is an accent, not a dominant fill.
  // Using colors.primary for chrome backgrounds was the original bug that
  // produced a solid-gold header.
  chrome: '#0A0F1E',

  textPrimary: '#ffffff',
  textSecondary: 'rgba(255, 255, 255, .62)',
  textDisabled: 'rgba(255, 255, 255, .38)',
  textOnPrimary: '#0A0F1E', // text/icons on filled gold — dark navy for contrast

  white: '#ffffff',
  black: '#000000',
  bgBase: '#0A0F1E',
  bgSecondary: '#0d1426',
  bgDisabled: '#111a31',
  overlay: 'rgba(0, 0, 0, 0.6)',

  success: '#22c55e',
  warning: '#eab308',
  error: '#ef4444',
  info: '#7dd3fc',

  borderSubtle: 'rgba(255, 255, 255, .08)',
  borderMedium: 'rgba(255, 255, 255, .08)',
  borderStrong: 'rgba(218, 148, 44, .30)',
  accentTintAlpha: 0.15,
};

// Light variant of the SAME gold brand as dark mode — sourced directly from
// the user-supplied [data-theme="light"] CSS export (the light counterpart to
// the dark palette's own source-of-truth block). Gold primary/secondary carry
// over unchanged from dark mode (not restated in the light block, since the
// brand accent doesn't change between modes); everything else below maps
// 1:1 to that block's --imported-* custom properties.
const lightRaw: RawPalette = {
  primary: '#DA942C',       // same gold as dark mode (--imported-primary, inherited)
  primaryLight: '#f0b558',
  secondary: '#b87216',
  accent: '#f0b558',

  // Fully light chrome (header/tab bar) — a light surface like the rest of
  // the app, not a dark navy bar and not filled gold (gold stays an accent,
  // never a dominant fill, same rule as dark mode's dedicated chrome token).
  chrome: '#ffffff',

  textPrimary: '#0A0F1E',              // --imported-foreground
  textSecondary: 'rgba(10, 15, 30, .62)', // --imported-muted-foreground
  textDisabled: 'rgba(10, 15, 30, .38)',  // same alpha step-down as dark mode's textDisabled
  textOnPrimary: '#0A0F1E',            // --imported-primary-foreground, inherited unchanged from the dark block (not restated in light) — same dark-navy-on-gold as dark mode

  white: '#ffffff',
  black: '#000000',
  bgBase: '#ffffff',                   // --imported-background
  bgSecondary: '#f8fafc',              // --background-2
  bgDisabled: '#f1f5f9',               // --background-3
  overlay: 'rgba(0, 0, 0, 0.5)',

  success: '#28a745',
  warning: '#ffc107',
  error: '#dc3545',
  info: '#17a2b8',

  borderSubtle: 'rgba(10, 15, 30, .10)',  // --imported-border
  borderMedium: 'rgba(10, 15, 30, .10)',  // --imported-border (dark mode uses the same value for both subtle/medium too)
  borderStrong: 'rgba(218, 148, 44, .45)', // --border-strong
  accentTintAlpha: 0.12,                   // --imported-accent
};

function buildSemanticColors(raw: RawPalette) {
  return {
    // --- New semantic groups (preferred going forward) ---
    background: {
      base: raw.bgBase,           // page background
      surface: raw.bgBase,        // card/elevated background
      surfaceRaised: raw.bgSecondary,  // modal/dropdown/tooltip layer
      // Legacy keys, same object — 245+ existing call sites use these directly.
      primary: raw.bgBase,
      secondary: raw.bgSecondary,
      disabled: raw.bgDisabled,
      overlay: raw.overlay,
    },
    text: {
      primary: raw.textPrimary,
      secondary: raw.textSecondary,
      disabled: raw.textDisabled,
      onPrimary: raw.textOnPrimary,
    },
    // Named borderTokens (not `border`) to avoid colliding with the flat
    // colors.border string alias below, which 641+ existing call sites use
    // directly as a single ColorValue (e.g. `borderColor: colors.border`).
    borderTokens: {
      default: raw.borderSubtle,
      subtle: raw.borderSubtle,
      medium: raw.borderMedium,
      strong: raw.borderStrong,
    },
    brand: {
      primary: raw.primary,
      primaryLight: raw.primaryLight,
      primaryDark: raw.secondary,
      secondary: raw.secondary,
      accent: raw.accent,
      chrome: raw.chrome,
    },
    status: {
      success: raw.success,
      warning: raw.warning,
      error: raw.error,
      info: raw.info,
    },
    literal: {
      white: raw.white,
      black: raw.black,
    },

    // --- Legacy flat aliases (kept for backward compatibility) ---
    primary: raw.primary,
    secondary: raw.secondary,
    accent: raw.accent,

    textDark: raw.textPrimary,
    textLight: raw.textSecondary,
    textSecondary: raw.textSecondary,

    white: raw.white,
    lightGray: raw.bgSecondary,

    success: raw.success,
    warning: raw.warning,
    error: raw.error,
    info: raw.info,

    border: raw.borderSubtle,
    borderLight: raw.borderSubtle,
    borderMedium: raw.borderMedium,
    borderDark: raw.borderStrong,
    borders: {
      light: raw.borderSubtle,
      medium: raw.borderMedium,
      dark: raw.borderStrong,
    },

    primaryTransparent: hexToRgba(String(raw.primary), raw.accentTintAlpha) as ColorValue,
    accentTransparent: hexToRgba(String(raw.primary), raw.accentTintAlpha) as ColorValue,

    primaryGradient: [raw.primary, raw.secondary] as [ColorValue, ColorValue],
    accentGradient: [raw.accent, raw.primary] as [ColorValue, ColorValue],
  };
}

function buildShadows(raw: RawPalette, mode: 'light' | 'dark') {
  if (mode === 'dark') {
    return {
      small: '0 2px 6px -2px rgba(0, 0, 0, .4)',
      medium: '0 10px 30px -10px rgba(0, 0, 0, .5)',
      large: '0 30px 80px -30px rgba(0, 0, 0, .6)',
      primary: `0 8px 24px -8px ${hexToRgba(String(raw.primary), 0.55)}, 0 0 0 1px ${hexToRgba(String(raw.primary), 0.15)}`,
      accent: `0 8px 24px -8px ${hexToRgba(String(raw.primary), 0.55)}, 0 0 0 1px ${hexToRgba(String(raw.primary), 0.15)}`,
    };
  }
  return {
    // Navy-tinted shadows (rgba(10, 15, 30, ...)), matching dark mode's shape/
    // offsets with the light theme's --shadow-sm/--shadow/--shadow-lg values.
    small: '0 2px 6px -2px rgba(10, 15, 30, .10)',
    medium: '0 10px 30px -10px rgba(10, 15, 30, .15)',
    large: '0 30px 80px -30px rgba(10, 15, 30, .18)',
    primary: `0 8px 24px -8px ${hexToRgba(String(raw.primary), 0.3)}, 0 0 0 1px ${hexToRgba(String(raw.primary), 0.12)}`,
    accent: `0 8px 24px -8px ${hexToRgba(String(raw.primary), 0.3)}, 0 0 0 1px ${hexToRgba(String(raw.primary), 0.12)}`,
  };
}

/** Reads the persisted theme preference SYNCHRONOUSLY on web, before any other
 * module (all of which call StyleSheet.create at import time — see the file
 * banner comment) gets a chance to bake in the wrong palette. Every
 * `StyleSheet.create()` call across the app runs during module import, which
 * happens before React ever renders — so ThemeContext.tsx's boot-time
 * setColorScheme() call runs far too late to affect them; only this
 * module-scope read, which happens as colors.ts itself is first imported
 * (necessarily before any file that imports FROM colors.ts can run its own
 * StyleSheet.create), is early enough. AsyncStorage-web is a thin wrapper over
 * localStorage, so a synchronous read is safe here specifically on web;
 * native doesn't have this race (a fresh native reload gets a fresh JS
 * context that re-evaluates this module fresh after storage is available). */
function readPersistedThemeModeSync(): 'light' | 'dark' {
  if (typeof window === 'undefined' || !window.localStorage) return 'dark';
  try {
    const raw = window.localStorage.getItem('sendifier-app-store');
    if (!raw) return 'dark';
    const parsed = JSON.parse(raw);
    const mode = parsed?.state?.themeMode;
    return mode === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

const initialMode = readPersistedThemeModeSync();
const initialRaw = initialMode === 'light' ? lightRaw : darkRaw;
let currentMode: 'light' | 'dark' = initialMode;

// Exposed so other theme modules (e.g. design-system.ts's shadow tokens) can
// branch on the same resolved boot-time mode without duplicating the
// sync-read race-avoidance logic documented above.
export function getThemeMode(): 'light' | 'dark' {
  return currentMode;
}

// Mutable, module-level objects — every existing `import { colors } from
// '../../theme'` (or shadows) keeps the same reference/shape forever;
// setColorScheme() below reassigns their contents in place.
export const colors = buildSemanticColors(initialRaw);
export const shadows = buildShadows(initialRaw, initialMode);

// Add shadow color for components
export const shadow = '#000000';

/** Switches the active palette in place. Must be called before any NEW module
 * evaluates its StyleSheet.create() calls to take effect there (i.e. on app
 * boot, before the rest of the tree renders) — does not repaint already-
 * rendered screens. See ThemeContext.tsx for the boot-time call and the
 * reload-on-toggle flow that makes a runtime mode change actually visible. */
export function setColorScheme(mode: 'light' | 'dark') {
  const raw = mode === 'dark' ? darkRaw : lightRaw;
  Object.assign(colors, buildSemanticColors(raw));
  Object.assign(shadows, buildShadows(raw, mode));
  currentMode = mode;
}

/**
 * A theme colour at a given opacity, whatever its format: '#rrggbb', '#rgb'
 * or 'rgb(a)(…)'. Appending a hex alpha ("+ '20'") only works for 6-digit
 * hex colours — several theme colours are rgba(), and that produced invalid
 * colours (invisible borders, missing badge backgrounds).
 */
export function withAlpha(color: unknown, alpha: number): string {
  const c = String(color ?? '').trim();
  const a = Math.max(0, Math.min(1, alpha));
  let m = c.match(/^#([0-9a-f]{6})([0-9a-f]{2})?$/i);
  if (m) return hexToRgba(`#${m[1]}`, a);
  m = c.match(/^#([0-9a-f])([0-9a-f])([0-9a-f])$/i);
  if (m) return hexToRgba(`#${m[1]}${m[1]}${m[2]}${m[2]}${m[3]}${m[3]}`, a);
  m = c.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/i);
  if (m) return `rgba(${m[1]}, ${m[2]}, ${m[3]}, ${a})`;
  return c;
}
